import React from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import Dropzone from "react-dropzone";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import shortid from "shortid";
import { ThunkDispatch } from "redux-thunk";
import { ApplicationAction, ApplicationState } from "../../../types";
import { I18NString } from "../../../types/modal";
import {
  FileValue,
  isSubject,
  LayoutNode,
  SubjectComment,
} from "../../../types/subject";
import {
  TABLE_CLASS,
  TABLE_HEADER_COLUMN_CLASS,
} from "../../admin/VirtualizedTable";
import { MSG_TABLE_NO_DATA_TO_DISPLAY } from "../../messages";
import {
  change,
  changeAt,
  confirmItemRemoveModal,
  registerUpload,
  remove,
} from "../../../actions/subject";
import { downloadFile } from "../../../actions/filearchive";
import { isCardEdit, isEditable } from "../../../services/layout";

import LargeInput from "./LargeInput";
import ToolbarInput from "./ToolbarInput";
import { Inplace } from "../../Inplace";

import { REMOVE_BUTTON_ICON_STYLE } from "./Style";
import styles from "./Table.module.css";

const COLUMNS = {
  $label: {
    key: "$label",
    data: {
      label: { id: "OBJECTCARD_FILE_TABLE_HEADER_NAME" },
      dataSort: true,
      width: 100,
      dataFormat: nameFormatter,
      default: true,
      inplaced: true,
    },
  },
  $description: {
    key: "$description",
    data: {
      width: 150,
      dataSort: true,
      default: true,
      label: { id: "OBJECTCARD_FILE_TABLE_HEADER_DESCRIPION" },
      inplaced: true,
    },
  },
  $contentType: {
    key: "$contentType",
    data: {
      label: { id: "OBJECTCARD_FILE_TABLE_HEADER_TYPE" },
      width: 80,
      dataSort: true,
      default: true,
    },
  },
  $contentSize: {
    key: "$contentSize",
    data: {
      label: { id: "OBJECTCARD_FILE_TABLE_HEADER_SIZE" },
      width: 60,
      dataSort: true,
      default: true,
    },
  },
  $sha1: {
    key: "$sha1",
    data: {
      label: { id: "OBJECTCARD_FILE_TABLE_HEADER_DOWNLOAD" },
      width: 40,
      dataFormat: refFormatter,
      formatExtraData: "this.props.contextPath",
      dataAlign: "center",
      default: true,
    },
  },
};

const CONTENT_SIZE = "$contentSize";
const CONTENT_TYPE = "$contentType";
const DESCRIPTION = "$description";
const LABEL = "$description";
const TMPNAME = "$tmpName";
const UPLOADKEY = "$uploadKey";
const SHA1 = "$sha1";

const dropZoneStyle = {
  style: {
    width: "auto",
    height: "100px",
    borderWidth: 2,
    borderColor: "#00ff00",
    borderStyle: "solid",
    borderRadius: 5,
    // backgroundColor: '#eee'
  },
  activeStyle: {
    borderStyle: "dashed",
    height: "100px",
    borderWidth: 2,
    borderRadius: 5,
    // backgroundColor: '#eee'
  },
  rejectStyle: {
    borderStyle: "solid",
    height: "100px",
    // backgroundColor: '#eee',
    borderWidth: 2,
    borderRadius: 5,
    borderColor: "#ff0000",
  },
};

const previewStyle = {
  height: "auto",
  width: "auto",
  maxWidth: "100px",
  maxHeight: "100px",
};

function refFormatter(
  cell: any,
  row: any,
  cp: string,
  download: (sha1: string) => void
) {
  if (!cell) {
    return "-";
  }
  let filename = row.$originalName;
  if (typeof filename == "undefined") {
    filename = row.$label;
  }

  return (
    <a href={`/rest/file/download/${cell}`} download={filename}>
      <i className="fa fa-download" aria-hidden="true"></i>
    </a>
  );
}

function getPreview(preview: any) {
  const src = URL.createObjectURL(preview);
  return (
    <img
      src={src}
      onLoad={() => URL.revokeObjectURL(src)}
      style={previewStyle}
    />
  );
}

function nameFormatter(cell: any, row: any) {
  if (row.preview) {
    return (
      <div>
        {cell} {getPreview(row.preview)}
      </div>
    );
  }
  return cell;
}

interface FileComponentProps {
  subjectKey: string;
  nodeId: string;
  visible: boolean;
  editable: boolean;
  cardEditable?: boolean;
  error?: string | I18NString;
  node?: LayoutNode;
  value: FileValue | FileValue[];
  contextPath: string;
  wasTableUpdated?: boolean;
  comment?: SubjectComment;
  download: (sha1: string) => void;
  registerUpload: (files: any, multiple: boolean) => void;
  change: (data: any, options?: any) => void;
  remove: (idxList: number[]) => void;
  confirmItemRemoveModal: (callback: () => void) => void;
  changeAt: (data: any, idx: number) => void;
}
interface FileComponentStates {
  selected: { [num: number]: string };
  columnsWidths: { [columnName: string]: number };
  draggedColumns: {
    col1: string;
    col2: string;
    x: number;
  };
}
class FileComponent extends React.Component<
  FileComponentProps,
  FileComponentStates
> {
  private fileMap: any;
  private headers: any = {};
  private rows: any[] = [];

  constructor(props: FileComponentProps) {
    super(props);
    this.onDrop = this.onDrop.bind(this);
    this.removeClicked = this.removeClicked.bind(this);

    let columnsWidths: { [name: string]: number } = {};
    // for (let colKey of COLUMNS) {
    //     columnsWidths[colKey.key] = colKey.width;
    // }
    this.state = {
      selected: {},
      columnsWidths: columnsWidths,
      draggedColumns: {
        col1: "",
        col2: "",
        x: 0,
      },
    };
  }

  removeClicked() {
    const { node } = this.props;

    if (!node) {
      return;
    }
    let data = this.props.value;
    if (node.multiple && Array.isArray(data)) {
      let indexList: number[] = [];
      data.forEach((d: any, idx: number) => {
        if (this.state.selected[idx + 1]) {
          indexList.push(idx);
        }
      });
      if (indexList.length > 0) {
        this.props.remove(indexList);
      }
    } else if (!Array.isArray(data)) {
      let key = data.$sha1 || data.$uploadKey;
      if (
        Object.keys(this.state.selected).length === 1 &&
        this.state.selected[1] === key
      ) {
        this.props.change({});
      }
    }
    this.setState({ selected: {} }); //clear selection
  }

  componentDidMount() {
    this.initHeader();
    this.initRows();
    document.addEventListener("mouseup", this.releaseResizer);
  }
  componentWillUnmount() {
    document.removeEventListener("mouseup", this.releaseResizer);
  }

  editInplace = (data: any) => {
    const { change } = this.props;
    change(data);
  };
  //Fix unkillable dragging
  clearSelection() {
    if (window.getSelection) {
      let selection = window.getSelection();
      if (selection == null) {
        return;
      }
      if (selection.empty) {
        // Chrome
        selection.empty();
      } else if (selection.removeAllRanges) {
        // Firefox
        selection.removeAllRanges();
      }
    }
  }
  ////////////////////DRAGGABLE COLUMNS/////////////////////////////

  //we need  right and left resizer because empty div element can't overlap vertical column border
  //so we add 2 empty div elements , vertical columns bars will be between pairs of resizers
  captureRightResizer(leftColumnName: string, e: any) {
    //finds index of the left column( columns with index+1 will be right column)
    e.preventDefault();
    this.clearSelection();
    let i = this.getColumnIndex(leftColumnName);

    if (i - 1 >= 0) {
      this.setState({
        draggedColumns: {
          col1: this.headers[i - 1].key,
          col2: leftColumnName,
          x: e.pageX,
        },
      });
    }
  }

  captureLeftResizer(leftColumnName: string, e: any) {
    //finds index of the left column( columns with index+1 will be right column)

    e.preventDefault();
    this.clearSelection();
    let i = this.getColumnIndex(leftColumnName);
    if (i + 1 < this.headers.length) {
      this.setState({
        draggedColumns: {
          col1: leftColumnName,
          col2: this.headers[i + 1].key,
          x: e.pageX,
        },
      });
    }
  }

  resiserIsBeingMoved(evt: any) {
    if (
      this.state.draggedColumns.col1 !== "" &&
      this.state.draggedColumns.col2 !== ""
    ) {
      evt.preventDefault();
      let oldX = this.state.draggedColumns.x;
      let newX = evt.pageX;
      let delta = newX - oldX;
      let col1 = this.state.draggedColumns.col1;
      let col2 = this.state.draggedColumns.col2;

      let col1NewWidth = this.state.columnsWidths[col1] + delta;
      let col2NewWidth = this.state.columnsWidths[col2] - delta;
      if (col1NewWidth > 10 && col2NewWidth > 10) {
        this.setState({
          draggedColumns: { ...this.state.draggedColumns, x: newX },
          columnsWidths: {
            ...this.state.columnsWidths,
            [col1]: col1NewWidth,
            [col2]: col2NewWidth,
          },
        });
      }
    }
  }
  releaseResizer = () => {
    this.setState({ draggedColumns: { col1: "", col2: "", x: 0 } });
  };
  getColumnIndex(columnName: string) {
    let i = 0;
    for (let col of this.headers) {
      if (col.key === columnName) {
        break;
      }
      i++;
    }
    return i;
  }
  //////////////////// END DRAGGABLE COLUMNS/////////////////////////////

  getToolbar() {
    const disabled = Object.keys(this.state.selected).length === 0;
    return (
      <ButtonGroup className="pull-right " role="group" aria-label="...">
        <Button
          style={{ zIndex: 0 }}
          disabled={disabled}
          variant="secondary"
          className="p-1"
          onClick={() => this.props.confirmItemRemoveModal(this.removeClicked)}
        >
          <i
            className="fa fa-minus-circle fa-fw"
            style={REMOVE_BUTTON_ICON_STYLE}
            aria-hidden="true"
          ></i>
          <FormattedMessage
            id="OBJECTCARD_FILE_REMOVE_BUTTON"
            defaultMessage="Remove"
            description="User should click this button to remove file from table"
          />
        </Button>
      </ButtonGroup>
    );
  }

  onDrop(acceptedFiles: File[]) {
    //Save for preview
    const { node } = this.props;
    if (!node || acceptedFiles.length === 0) {
      return;
    }
    if (!this.fileMap) {
      this.fileMap = {};
    }
    for (let file of acceptedFiles) {
      this.fileMap[file.name] = file;
    }

    this.props.registerUpload(acceptedFiles, node.multiple || false);
    // updComp(true);
  }

  getZone(
    isDragReject: boolean,
    isDragActive: Boolean,
    isDragAccept: boolean,
    getRootProps: any,
    getInputProps: any
  ) {
    return (
      <div
        className="npt-drop-box bg-white"
        style={
          isDragReject
            ? dropZoneStyle.rejectStyle
            : isDragAccept
            ? dropZoneStyle.style
            : dropZoneStyle.activeStyle
        }
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <FormattedMessage
          id="OBJECTCARD_FILE_DROP_FILE_HERE"
          defaultMessage="Try to drop file here, or click to select file to upload"
          description="Inform user how to upload file"
        />
      </div>
    );
  }

  getFooter() {
    const { node } = this.props;
    if (!node) {
      return null;
    }
    return (
      <Dropzone multiple={node.multiple} onDrop={this.onDrop}>
        {({
          getRootProps,
          isDragActive,
          isDragAccept,
          isDragReject,
          getInputProps,
        }) =>
          this.getZone(
            isDragReject,
            isDragActive,
            isDragAccept,
            getRootProps,
            getInputProps
          )
        }
      </Dropzone>
    );
  }

  initRows() {
    //Get data
    const { node, value } = this.props;
    let data: FileValue[] = Array.isArray(value) ? value : [value];
    if (!node) {
      this.rows = [];
      return;
    }
    if (node?.multiple) {
      if (!Array.isArray(value)) {
        data = [];
      }
    } else {
      if (typeof value !== "object") {
        data = [];
      } else {
        //Put value into the array
        if (
          value &&
          !Array.isArray(value) &&
          (value.$sha1 || value.$uploadKey)
        ) {
          //check if is not empty object
          data = [value];
        } else {
          data = [];
        }
      }
    }
    let fileMap = this.fileMap;
    data = data.map(function (row: any, idx: number) {
      let preview = null;
      if (row.$tmpName && fileMap) {
        let file = fileMap[row.$tmpName];
        // preview = file.preview;
        preview = file;
      }

      let key = row.$sha1;
      if (typeof key == "undefined") {
        key = row.$uploadKey;
      }
      const num = idx + 1;
      return Object.assign({}, row, { key, preview, num });
    });

    this.rows = [...data];
  }

  getInitialColumnsWidths() {
    const colmnsWidths: { [key: string]: number } = {};
    for (const idx in this.headers) {
      const label = this.headers[idx].label;
      const key = this.headers[idx].key;
      colmnsWidths[key] = this.headers[idx].width || 40;
    }
    return colmnsWidths;
  }
  setWidthAfterRisizePage(evt: any) {
    let width = evt.width;
    if (width === 0) {
      return width;
    }
    const { columnsWidths } = this.state;
    let columnsWidthsCopy;
    // columnsWidthsCopy = this.getInitialColumnsWidths();

    const widthCount = Object.keys(columnsWidths).length;
    if (widthCount === 0 || this.headers.length !== widthCount) {
      columnsWidthsCopy = this.getInitialColumnsWidths();
    } else {
      columnsWidthsCopy = { ...columnsWidths };
    }

    let columnsWidthSum = 0;
    for (let col in columnsWidthsCopy) {
      columnsWidthSum += columnsWidthsCopy[col];
    }
    for (let col in columnsWidthsCopy) {
      let perc = (columnsWidthsCopy[col] * 100) / columnsWidthSum;
      let factW = Math.round((width * perc) / 100);
      columnsWidthsCopy[col] = factW;
    }
    this.setState({
      columnsWidths: columnsWidthsCopy,
    });
    return width;
  }
  setColumnWidth(col: any) {
    return this.state.columnsWidths[col];
  }
  selectHandler = (num: number, id: string) => {
    let newSelected: { [num: number]: string } = { ...this.state.selected };
    if (newSelected[num]) {
      delete newSelected[num];
    } else {
      newSelected[num] = id;
    }
    this.setState({ selected: newSelected });
  };
  selectAll = () => {
    const { value } = this.props;
    const { selected } = this.state;
    if (Array.isArray(value)) {
      if (Object.keys(selected).length !== 0) {
        this.setState({ selected: {} });
        return;
      }
      let newSelected = { ...selected };
      value.forEach((v: any, idx: number) => {
        const val = v.$sha1 || v.$uploadKey;
        if (val) {
          newSelected[idx + 1] = val;
        }
      });
      this.setState({ selected: newSelected });
    } else {
      const val = value.$uploadKey || value.$sha1;

      !val || Object.keys(selected).length !== 0
        ? this.setState({ selected: {} })
        : this.setState({ selected: { 1: val } });
    }
  };
  //if grid width equals to header width
  //right borders of body rows is not visible
  //so we increase   width of grid a bit (to 1 %)
  gridStyle(width: number, height: number) {
    const perc = width / 100;
    const newWidth = width + perc + "px";
    return { width: newWidth, height: height || 40 };
  }

  rowClassName = ({ index }: any) => {
    const { editable } = this.props;
    const className = editable ? styles.rowHover : "";
    const selectedStyle = this.state.selected[index + 1]
      ? styles.selectedRow
      : "";
    if (index < 0) {
      return `${styles.headerRow}   text-dark ${selectedStyle}`;
    } else if (index % 2 !== 0) {
      // return TABLE_BODY_ROW_CLASS;
      return `   ${styles.bodyRow} ${className}`;
    } else {
      return ` ${styles.bodyRow} ${className}`;
    }
  };
  initHeader() {
    if (Object.keys(this.headers).length !== 0) {
      return this.headers;
    }
    this.headers = {
      ["key"]: { key: "key", data: { width: 15, hidden: true } },
      ...COLUMNS,
    };
    return this.headers;
  }
  addCheckable() {
    if (this.props.editable && !this.headers["check"]) {
      const headerEntries = Object.entries(this.headers);
      headerEntries.unshift(["check", { key: "check", checkable: true }]);
      this.headers = Object.fromEntries(headerEntries);
    } else if (!this.props.editable && this.headers["check"]) {
      delete this.headers["check"];
    }
  }
  render() {
    const {
      error,
      editable,
      cardEditable,
      node,
      contextPath,
      visible,
      download,
      nodeId,
      comment,
    } = this.props;
    let optional = {};
    if (!node) {
      return null;
    }
    const tableStye = {
      margin: "0px",
      borderRadius: "0px",
    };

    let containerStyle = {
      overflowX: "auto",
    };
    let options = {
      noDataText: MSG_TABLE_NO_DATA_TO_DISPLAY,
    };
    this.initHeader();
    this.initRows();
    this.addCheckable();

    const table = (
      <TableComponent
        downloadFile={download}
        nodeId={nodeId}
        editInplace={this.editInplace}
        columns={this.headers}
        rows={this.rows}
        contextPath={contextPath}
        editable={editable}
        getDropZone={this.getFooter.bind(this)}
        select={this.selectHandler}
        selectAll={this.selectAll}
        selected={this.state.selected}
      />
    );
    if (editable) {
      return (
        <ToolbarInput
          id={this.props.subjectKey + "." + this.props.nodeId}
          node={node}
          editable={editable}
          cardEditable={cardEditable}
          error={error}
          visible={visible}
          comment={comment}
        >
          {this.getToolbar()}
          {table}
        </ToolbarInput>
      );
    }
    return (
      node && (
        <LargeInput
          id={this.props.subjectKey + "." + this.props.nodeId}
          node={node}
          editable={editable}
          cardEditable={cardEditable}
          error={error}
          visible={visible}
          comment={comment}
        >
          {table}
        </LargeInput>
      )
    );
  }
}

export default connect(
  (
    state: ApplicationState,
    ownProps: { subjectKey: string; nodeId: string }
  ) => {
    const subject =
      state.subject && state.subject.subjects[ownProps.subjectKey];
    const { subjectKey, nodeId } = ownProps;
    if (!isSubject(subject)) {
      return {
        contextPath: state.location.contextPath,
        visible: false,
        editable: false,
      };
    }
    const node = subject && subject.nodeById[nodeId];
    const value = subject && subject.values[nodeId];
    const cardEditable = isCardEdit(subject);
    const editable = isEditable(subject, nodeId, cardEditable);
    const error = subject.validation[nodeId];
    const wasTableUpdated =
      subject.componentUpdated && subject.componentUpdated[nodeId];
    const visible = subject.visibility[nodeId] ? true : false;

    return {
      node,
      value,
      editable,
      cardEditable,
      error,
      contextPath: state.location.contextPath,
      wasTableUpdated,
      visible,
      comment: subject.comment[nodeId],
    };
  },
  (
    dispatch: ThunkDispatch<ApplicationState, {}, ApplicationAction>,
    ownProps: { subjectKey: string; nodeId: string }
  ) => {
    const { subjectKey, nodeId } = ownProps;
    return {
      download: (sha1: string) => dispatch(downloadFile(sha1)),
      registerUpload: (files: any, multiple: boolean) =>
        dispatch(registerUpload(subjectKey, nodeId, files, multiple)),
      change: (data: any, options?: any) =>
        dispatch(change(subjectKey, nodeId, data, options)),
      changeAt: (data: any, idx: number) =>
        dispatch(changeAt(subjectKey, nodeId, idx, data)),
      remove: (idxList: number[]) =>
        dispatch(remove(subjectKey, nodeId, idxList)),
      confirmItemRemoveModal: (callback: () => void) =>
        dispatch(confirmItemRemoveModal(callback)),
    };
  }
)(FileComponent);

interface TableComponentProps {
  nodeId: string;
  columns: any;
  rows: any[];
  selected?: { [num: number]: string };
  select: (num: number, id: string) => void;
  selectAll: () => void;
  editable: boolean;
  getDropZone: () => any;
  downloadFile: (sha1: string) => any;
  editInplace: (data: any) => void;
  contextPath: string;
}
class TableComponent extends React.Component<TableComponentProps> {
  constructor(props: TableComponentProps) {
    super(props);
    this.bodyCellRenderer = this.bodyCellRenderer.bind(this);
  }

  noDataRowRenderer() {
    const { columns } = this.props;
    const colspan = Object.values(columns).length;
    return (
      <tr>
        <th colSpan={colspan}>
          <div className="text-center">{MSG_TABLE_NO_DATA_TO_DISPLAY}</div>
        </th>
      </tr>
    );
  }
  gridHeight(rows: any[]) {
    let height = 0;
    rows.forEach((r) => {
      if (r.preview) {
        height += 110;
      } else {
        height += 40;
      }
    });
    return height;
  }
  estimateTableHeight(rows: any[]) {
    let height = 40;
    if (rows.length === 0) {
      return 80;
    }

    return height + this.gridHeight(rows);
  }
  isColumnHidden(col: any) {
    return col?.data?.hidden;
  }
  renderHeaderColumn = (columnData: any) => {
    if (columnData.checkable) {
      const checked =
        this.props.selected &&
        this.props.rows.length > 0 &&
        this.props.rows.length === Object.keys(this.props.selected).length;

      return (
        <div className="pl-1">
          {" "}
          <input
            className={styles.check}
            type="checkbox"
            checked={checked}
            onChange={() => this.props.selectAll()}
          />
        </div>
      );
    }
    if (!columnData?.data) {
      return null;
    }

    const label = columnData.data.label || columnData.key;
    if (!label) {
      return "";
    }
    if (typeof label === "string") {
      return label;
    } else {
      return <FormattedMessage id={label.id} />;
    }
  };
  headerCellRenderer = (data: any) => {
    const cell = this.renderHeaderColumn(data);

    return (
      <th
        key={data?.key}
        scope="col px-2"
        className={`npt-table-header-column`}
      >
        <div className="w-100 d-flex flex-column justify-content-center">
          {cell}
        </div>
      </th>
    );
  };
  renderHeaderColumns() {
    const { columns, rows } = this.props;
    const headerColumns: any[] = [];
    for (let c of Object.values(columns)) {
      const column = c as any;
      if (!this.isColumnHidden(column)) {
        headerColumns.push(this.headerCellRenderer(column));
      }
    }
    return headerColumns;
  }
  changeLabel = (key: string, data: any) => (e: any) => {
    const { editInplace } = this.props;
    const text = e.target.value;
    data[key] = text;
    editInplace(data);
  };
  bodyCellRenderer = (key: string, columnData: any, data: any) => {
    const { columns, selected, select, rows, downloadFile, editable } =
      this.props;
    // console.log('key,columnData', key, columnData, data)
    if (columnData.checkable) {
      const checked = selected && selected[data.num];
      return (
        <td key={key}>
          <div className="pl-1">
            {" "}
            <input
              className={styles.check}
              type="checkbox"
              checked={!!checked}
              onChange={() => {
                const rdfId = data.key;
                select(data.num, rdfId);
              }}
            />
          </div>
        </td>
      );
    }
    let label = data[key];

    if (!label) {
      return <td key={key}> </td>;
    }

    const dataFormat = columnData?.data?.dataFormat;
    const inplaced = columnData?.data?.inplaced;

    if (dataFormat) {
      label = dataFormat(label, data, this.props.contextPath, downloadFile);
    }
    const rdfId = data.key;
    if (inplaced && editable) {
      const newKey = key.replace("$", "");
      label = (
        <Inplace
          style={{ width: "100%" }}
          id={`id${shortid.generate()}`}
          text={data[key]}
          change={this.changeLabel(key, data)}
        />
      );
    }

    return (
      <td key={key}>
        <div>{label}</div>
      </td>
    );
  };

  bodyRowRenderer(row: any, index: number) {
    const { columns } = this.props;
    const rowData = [];
    const colEntries = Object.entries(columns);
    for (let c of colEntries) {
      const [key, value] = c;
      const column = value as any;
      const hidden = column?.data?.hidden;
      if (hidden) {
        continue;
      }
      rowData.push(this.bodyCellRenderer(key, value, row));
    }
    return <tr key={index}>{rowData}</tr>;
  }

  render() {
    const { columns, rows, editable, getDropZone } = this.props;

    return (
      <div
        style={{ overflowX: "auto" }}
        className={`${TABLE_CLASS}   ${styles.cardTable} `}
      >
        <div
          style={{ overflowX: "auto", maxHeight: "30rem" }}
          className="rounded  bg-white   w-100 "
        >
          <table
            className={`table mb-0 table-bordered table-hover ${styles.table}`}
          >
            <thead className={styles.header}>
              <tr>{this.renderHeaderColumns()}</tr>
            </thead>
            <tbody>
              {rows.length === 0
                ? this.noDataRowRenderer()
                : rows.map((r, idx) => this.bodyRowRenderer(r, idx))}
            </tbody>
          </table>
        </div>
        <div>{editable && getDropZone()}</div>
      </div>
    );
  }
}
